import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { ContactInfo, PhoneType, PhoneTypeValue, PhoneTypeValueEntries } from 'src/app/store/application';

@Component({
  selector: 'app-contact-entry',
  templateUrl: './contact-entry.component.html',
  styleUrls: ['./contact-entry.component.scss']
})
export class ContactEntryComponent implements OnInit, OnChanges {
  @Input() contactDetails: ContactInfo;
  @Input() addSecondPhone: boolean;
  @Input() addThirdPhone: boolean;
  @Input() contactInfoRequired: boolean;
  @Input() canCreateCodeCase: boolean;
  @Input() isErrorPresentOnPage: boolean;
  @Input() nameEmailReqOnly: boolean;
  @Input() nameAddressEmailReqOnly: boolean;

  @Output() newContact = new EventEmitter<ContactInfo>();
  @Output() addPhoneNumber = new EventEmitter();
  @Output() removePhoneNumber = new EventEmitter<number>();
  @Output() contactFormValidState = new EventEmitter<boolean>();

  contactForm: FormGroup;
  notificationSwitchToggleState: boolean = false;

  firstNameError: boolean = false;
  lastNameError: boolean = false;
  addressError: boolean = false;
  emailError: boolean = false;

  phoneNumberType1Error: boolean = false;
  phoneNumber1Error: boolean = false;

  phoneNumberType2Error: boolean = false;
  phoneNumber2Error: boolean = false;

  phoneNumberType3Error: boolean = false;
  phoneNumber3Error: boolean = false;

  phoneTypes: PhoneTypeValueEntries[] = [
    {typeName: 'Cell', phoneTypeValue: PhoneTypeValue.Cell},
    {typeName: 'Home', phoneTypeValue: PhoneTypeValue.Home},
    {typeName: 'Work', phoneTypeValue: PhoneTypeValue.Work},
    {typeName: 'Other', phoneTypeValue: PhoneTypeValue.Other}
  ];

  constructor() { }

  ngOnInit(): void {
    this.contactForm = this.buildFormGroup();
    this.contactForm.controls['phoneTypeControl'].setValue(this.phoneTypes[0].phoneTypeValue);

    if (this.contactDetails) {
      this.handleFormAddControl();
      this.populateFormGroup(this.contactForm, this.contactDetails);

      let isValid = this.contactForm.valid;
      let emailNotificationsValid = this.checkEmailNotificationValid();
      let phoneDetailsCorrect = this.checkPhonesCorrect(this.contactDetails.phone);

      this.contactFormValidState.emit(isValid && emailNotificationsValid && phoneDetailsCorrect);
    } else {
      this.contactFormValidState.emit(this.contactForm.valid);
    }
  }

  ngOnChanges() {
    this.handleFormAddControl();

    if (this.contactDetails && this.contactForm) {
      this.populateFormGroup(this.contactForm, this.contactDetails);
      this.contactFormValidState.emit(this.contactForm.valid && this.checkPhonesCorrect(this.contactDetails.phone) && this.checkEmailNotificationValid());
    }
  }

  handleFormAddControl() {
    if (this.contactForm) {

      // check if we want to add a secondary contact number
      if (this.addSecondPhone) {
        if (!this.contactForm.contains('phoneTypeControl2') && !this.contactForm.contains('phoneNumberControl2')) {
          this.contactForm.addControl('phoneNumberControl2', new FormControl('', [Validators.required, Validators.pattern("^\\([0-9]{3}\\) [0-9]{3}-[0-9]{4}")]));

          // check if this is going to be a required field or not
          if (this.contactInfoRequired) {
            this.contactForm.addControl('phoneTypeControl2', new FormControl(this.phoneTypes[1].phoneTypeValue, Validators.required));
          } else {
            this.contactForm.addControl('phoneTypeControl2', new FormControl(this.phoneTypes[1].phoneTypeValue));
          }
        }

        // we are trying to delete the second phone number instead
      } else {
        if (this.contactForm.contains('phoneTypeControl2') || this.contactForm.contains('phoneNumberControl2')) {
          this.contactForm.removeControl('phoneTypeControl2');
          this.contactForm.removeControl('phoneNumberControl2');
        }
      }

      // check if we want to add a third contact number
      if (this.addThirdPhone) {
        this.contactForm.addControl('phoneNumberControl3', new FormControl('', [Validators.required, Validators.pattern("^\\([0-9]{3}\\) [0-9]{3}-[0-9]{4}")]));

        // check if it is a required field or not
        if (this.contactInfoRequired) {
          this.contactForm.addControl('phoneTypeControl3', new FormControl(this.phoneTypes[2].phoneTypeValue, Validators.required));
        } else {
          this.contactForm.addControl('phoneTypeControl3', new FormControl(this.phoneTypes[2].phoneTypeValue));
        }

        // we are trying to delete the third phone number instead
      } else {
        if (this.contactForm.contains('phoneTypeControl3') || this.contactForm.contains('phoneNumberControl3')) {
          this.contactForm.removeControl('phoneTypeControl3');
          this.contactForm.removeControl('phoneNumberControl3');
        }
      }
    }
  }

  getNewNormalFormGroup() {
    return new FormGroup({
      firstNameControl: new FormControl(''),
      lastNameControl: new FormControl(''),
      emailControl: new FormControl('', Validators.email),
      phoneTypeControl: new FormControl(null),
      phoneNumberControl: new FormControl('', Validators.pattern("^\\([0-9]{3}\\) [0-9]{3}-[0-9]{4}")),
      notificationBox: new FormControl({value: false})
    });
  }
  
  getNewNormalRequiredFormGroup() {
    return new FormGroup({
      firstNameControl: new FormControl('', Validators.required),
      lastNameControl: new FormControl('', Validators.required),
      emailControl: new FormControl('', Validators.email),
      phoneTypeControl: new FormControl(null, Validators.required),
      phoneNumberControl: new FormControl('', [Validators.required, Validators.pattern("^\\([0-9]{3}\\) [0-9]{3}-[0-9]{4}")]),
      addressControl: new FormControl('', Validators.required),
      notificationBox: new FormControl({value: false})
    });
  }

  getNewNameEmailReqFormGroup() {
    let formGroup = this.getNewNormalFormGroup();
    formGroup.controls['firstNameControl'].addValidators(Validators.required);
    formGroup.controls['lastNameControl'].addValidators(Validators.required);
    formGroup.controls['emailControl'].addValidators([Validators.email, Validators.required]);
    return formGroup;
  }

  getNewNameAddressEmailReqFormGroup() {
    let formGroup = this.getNewNormalFormGroup();
    formGroup.controls['firstNameControl'].addValidators(Validators.required);
    formGroup.controls['lastNameControl'].addValidators(Validators.required);
    formGroup.controls['emailControl'].addValidators([Validators.email, Validators.required]);
    formGroup.addControl("addressControl", new FormControl('', Validators.required));
    return formGroup;
  }

  buildFormGroup() {
    if (!this.contactInfoRequired) return this.getNewNormalFormGroup();
    if (this.nameEmailReqOnly) return this.getNewNameEmailReqFormGroup();
    if (this.nameAddressEmailReqOnly) return this.getNewNameAddressEmailReqFormGroup();

    return this.getNewNormalRequiredFormGroup();  
  }

  handleKeypress(event: KeyboardEvent, targetId?: string) {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      if (!!targetId) {
        document.getElementById(targetId)?.click();
      } else {
        (event.target as any).click();
      }
    }
  }

  handleMouseDown(event: MouseEvent) {
    event.preventDefault();
  }

  populateFormGroup(contactForm: FormGroup, contactDetails: ContactInfo) {
    contactForm.get('firstNameControl')?.setValue(contactDetails.firstName);
    contactForm.get('lastNameControl')?.setValue(contactDetails.lastName);
    contactForm.get('emailControl')?.setValue(contactDetails.email);
    contactForm.get("notificationBox")?.setValue(contactDetails.notificationsRequested);

    this.handleNotificationControl();

    if (contactForm.contains('addressControl')) {
      contactForm.get('addressControl')?.setValue(contactDetails.address);
    }

    if (contactDetails.phone.length > 0) {
      let phone: PhoneType = contactDetails.phone[0];
      contactForm.get('phoneTypeControl')?.setValue(phone?.phoneType || null);
      contactForm.get('phoneNumberControl')?.setValue(this.getPhoneFormatting(phone?.phoneNumber || ""));

      if (this.addSecondPhone && contactDetails.phone[1]) {
        let phone = contactDetails.phone[1];
        contactForm.get('phoneTypeControl2')?.setValue(phone?.phoneType || null);
        contactForm.get('phoneNumberControl2')?.setValue(this.getPhoneFormatting(phone?.phoneNumber || ""));
      }

      if (this.addThirdPhone && contactDetails.phone[2]) {
        let phone = contactDetails.phone[2];
        contactForm.get('phoneTypeControl3')?.setValue(phone?.phoneType || null);
        contactForm.get('phoneNumberControl3')?.setValue(this.getPhoneFormatting(phone?.phoneNumber || ""));
      }
    }
  }

  setControlSpecificErrors(controlName: string, interactedWith: boolean = false) {
    let control = this.contactForm.controls[controlName];
    switch (controlName) {
      case 'firstNameControl':
        this.firstNameError = (interactedWith && this.contactInfoRequired) || control.invalid;
        break;

      case 'lastNameControl':
        this.lastNameError = (interactedWith && this.contactInfoRequired) || control.invalid;
        break;

      case 'addressControl':
        this.addressError = (interactedWith && this.contactInfoRequired) || control.invalid;
        break;

      case 'notificationBox':
      case 'emailControl':
        let emailControl = this.contactForm.controls['emailControl'];
        this.emailError = (this.notificationSwitchToggleState && emailControl.value === "") || emailControl.invalid;
        break;

      case 'phoneNumberControl':
        // check number length 14 because of number formatting
        this.phoneNumber1Error = control.invalid;
        break;

      case 'phoneNumberControl2':
        // check number length 14 because of number formatting
        this.phoneNumber2Error = control.invalid;
        break;

      case 'phoneNumberControl3':
        // check number length 14 because of number formatting
        this.phoneNumber3Error = control.invalid;
        break;

      default:
        break;
    }
  }

  getControlInvalid(controlName: string) {
    if (!this.isErrorPresentOnPage) {
      return false;  
    }

    this.setControlSpecificErrors(controlName);
    return this.isErrorPresentOnPage && this.contactForm.controls[controlName].invalid;
  }

  isNumericInput(event: any) {
    const key = event.keyCode;
    return ((key >= 48 && key <= 57) || // Allow number line
        (key >= 96 && key <= 105) // Allow number pad
    );
  };

  isModifierKey(event: any) {
    const key = event.keyCode;
    return (event.shiftKey === true || key === 35 || key === 36) || // Allow Shift, Home, End
      (key === 8 || key === 9 || key === 13 || key === 46) ||       // Allow Backspace, Tab, Enter, Delete
      (key > 36 && key < 41) ||                                     // Allow left, up, right, down
      (
          (event.ctrlKey === true || event.metaKey === true) &&     // Allow Ctrl/Command + A,C,V,X,Z
          (key === 65 || key === 67 || key === 86 || key === 88 || key === 90)
      );
  };

  enforceFormat(event: any) {
    // Input must be of a valid number format or a modifier key, and not longer than ten digits
    if (!this.isNumericInput(event) && !this.isModifierKey(event)){
      event.preventDefault();
    }
  };

  getPhoneFormatting(phoneNumber: string = ""): string {
    const areaCode = phoneNumber.substring(0, 3);
    const middle = phoneNumber.substring(3, 6);
    const last = phoneNumber.substring(6, 10);

    if (phoneNumber.length > 6) { return `(${areaCode}) ${middle}-${last}`; }
    else if (phoneNumber.length > 3) { return `(${areaCode}) ${middle}`; }
    else { return `${areaCode}`; }
  }

  formatToPhone(event: any) {
    if (this.isModifierKey(event)) { return; }
    const input = event.target.value.replace(/\D/g,'').substring(0,10); // First ten digits of input only
    event.target.value = this.getPhoneFormatting(input);
  };

  formatToNumber(number: string): string {
    return number.replace(/\D/g,'').substring(0,10);
  }

  fixPhoneNumberFormatting() {
    let phoneNumber: string = this.contactForm.get('phoneNumberControl')?.value;
    if (phoneNumber.length > 0 && !phoneNumber.includes("(")) {
      this.contactForm.get('phoneNumberControl')?.setValue(this.getPhoneFormatting(phoneNumber));
    }

    if (this.addSecondPhone) {
      let phoneNumber2: string = this.contactForm.get('phoneNumberControl2')?.value;
      if (phoneNumber2.length > 0 && !phoneNumber2.includes("(")) {
        this.contactForm.get('phoneNumberControl2')?.setValue(this.getPhoneFormatting(phoneNumber2));
      }
    }

    if (this.addThirdPhone) {
      let phoneNumber3: string = this.contactForm.get('phoneNumberControl3')?.value;
      if (phoneNumber3.length > 0 && !phoneNumber3.includes("(")) {
        this.contactForm.get('phoneNumberControl3')?.setValue(this.getPhoneFormatting(phoneNumber3));
      }
    }
  }

  handleNotificationControl() {
    this.notificationSwitchToggleState = this.contactForm.get('notificationBox')?.value;

    // if (this.notificationSwitchToggleState) {
    //   document.getElementById("switchState")!.innerText = "ON";
    // } else {
    //   document.getElementById("switchState")!.innerText = "OFF";
    // }
  }

  handleCheckErrors(event: any) {
    let key = event.target?.getAttribute('formControlName');
    if (!this.contactForm.contains(key)) {
      return;
    }

    let control = this.contactForm.controls[key];
    let interactedWith = control.touched && control.value === "";
    this.setControlSpecificErrors(key, interactedWith);
  }

  prunePhoneDetails() {
    let phones: PhoneType[] = [];
    let phoneType = this.contactForm.get('phoneTypeControl')?.value;
    let phoneNumber = this.formatToNumber(this.contactForm.get('phoneNumberControl')?.value);

    phones.push({
      phoneType,
      phoneNumber
    });

    if (this.addSecondPhone) {
      phoneType = this.contactForm.get("phoneTypeControl2")?.value;
      phoneNumber = this.formatToNumber(this.contactForm.get('phoneNumberControl2')?.value);

      phones.push({
        phoneType,
        phoneNumber
      });
    }

    if (this.addThirdPhone) {
      phoneType = this.contactForm.get("phoneTypeControl3")?.value;
      phoneNumber = this.formatToNumber(this.contactForm.get('phoneNumberControl3')?.value);

      phones.push({
        phoneType,
        phoneNumber
      });
    }

    // do not submit an empty phone if there is only one as the "optional" entries will cause the goToNext page check to disable the button
    if (phones.length === 1 && phones[0].phoneNumber.length === 0) {
      phones = [];
    }

    return phones;
  }

  checkPhonesCorrect(phoneArr: PhoneType[]) {
    if (phoneArr.length === 0) {
      return true;
    }

    let phonesCorrect = true;
    phoneArr.forEach(phone => phonesCorrect = phonesCorrect && phone.phoneNumber.length === 10);
    return phonesCorrect;
  }

  checkEmailNotificationValid() {
    if (this.notificationSwitchToggleState) {
      let control = this.contactForm.controls['emailControl'];
      let isValid = control.value !== "";
      this.emailError = !isValid;
      return isValid;
    }

    return true;
  }

  updateContactInfo(event: any) {
    this.fixPhoneNumberFormatting();
    this.handleNotificationControl();
    this.handleCheckErrors(event);

    const newContact: ContactInfo = {
      firstName: this.contactForm.get('firstNameControl')?.value,
      lastName: this.contactForm.get('lastNameControl')?.value,
      email: this.contactForm.get('emailControl')?.value,
      address: this.contactForm.contains('addressControl') ? this.contactForm.get('addressControl')?.value : "",
      phone: this.prunePhoneDetails(),
      notificationsRequested: this.notificationSwitchToggleState
    };

    this.newContact.emit(newContact);
    this.contactFormValidState.emit(this.contactForm.valid && this.checkPhonesCorrect(newContact.phone) && this.checkEmailNotificationValid());
  }

  removePhoneBtn(numberToRemove: number) {
    this.removePhoneNumber.emit(numberToRemove);
  }

  addPhoneBtn() {
    this.addPhoneNumber.emit();
  }
}
